/**
 * Global Notification Configuration
 *
 * Centralized configuration for toast notifications across the application.
 * This ensures consistent styling and behavior for all notifications.
 */

import type { CSSProperties } from 'react';
import type { ToastPosition } from 'react-hot-toast';

export interface NotificationConfig {
  /** Global position for all toasts */
  position: ToastPosition;
  /** Default duration in milliseconds */
  duration: number;
  /** Maximum number of toasts to show at once */
  maxToasts: number;
  /** Base styles applied to all toasts */
  baseStyle: CSSProperties;
  /** Variant-specific styles */
  variants: {
    success: CSSProperties;
    error: CSSProperties;
    warning: CSSProperties;
    info: CSSProperties;
    loading: CSSProperties;
  };
  /** Animation configuration */
  animation: {
    /** Entry animation duration in ms */
    enter: number;
    /** Exit animation duration in ms */
    exit: number;
  };
}

export const notificationConfig: NotificationConfig = {
  // Position toasts at top-right of viewport
  position: 'top-right',
  
  // Default duration: 4 seconds
  duration: 4000,
  
  // Maximum 3 toasts visible at once
  maxToasts: 3,
  
  // Base styles applied to all toasts
  baseStyle: {
    padding: '16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    maxWidth: '420px',
    wordBreak: 'break-word',
  },
  
  // Variant-specific styles (merged with baseStyle)
  variants: {
    success: {
      background: 'white',
      color: '#10b981', // green-500
      border: '2px solid #10b981', // green-500
    },
    error: {
      background: 'white',
      color: '#ef4444', // red-500
      border: '2px solid #ef4444', // red-500
    },
    warning: {
      background: 'white',
      color: '#f59e0b', // amber-500
      border: '2px solid #f59e0b', // amber-500
    },
    info: {
      background: 'white',
      color: '#3b82f6', // blue-500
      border: '2px solid #3b82f6', // blue-500
    },
    loading: {
      background: 'white',
      color: '#6b7280', // gray-500
      border: '2px solid #6b7280', // gray-500
    },
  },
  
  // Animation timings
  animation: {
    enter: 200,
    exit: 300,
  },
};

/**
 * Icon configurations for different toast types
 */
export const notificationIcons = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
  loading: '⏳',
} as const;

/**
 * Sound configurations (optional - for accessibility)
 */
export const notificationSounds = {
  enableSounds: false, // Can be toggled via user preferences
  sounds: {
    success: '/sounds/success.mp3',
    error: '/sounds/error.mp3',
    warning: '/sounds/warning.mp3',
    info: '/sounds/info.mp3',
  },
} as const;

/**
 * Accessibility configurations
 */
export const notificationA11y = {
  // ARIA role for screen readers
  role: 'alert',
  // Announce to screen readers immediately
  ariaLive: 'assertive' as const,
  // Ensure toasts are keyboard navigable
  tabIndex: 0,
} as const;